import React, { useState } from "react";
import { ExpandableCard } from "collections/Cards";
import { useTranslation } from "utilities/i18n";
import { PolyIcon } from "elements";
import { NetworkInfo } from "./NetworkInfo";
import { PrimaryDeviceInfoSection } from "./PrimaryDeviceInfo";
import { MAX_SEARCH_CHARS } from "Constants";
import {
  columnTranslations as pdiTranslations,
  PrimaryDeviceInfo
} from "./PDIHelpers";
import {
  columnTranslations as niTranslations,
  NetworkInfoTranslateKeys
} from "./NetworkInfoHelpers";
import "./DeviceInformation.scss";
import { ServiceInformation } from "./ServiceInformation";

/**
 * Returns an iterable key value pair array that is filtered on the filterTerm
 *
 * PDI = Primary Device Info
 * NI = Network Information
 * SI = Service Information
 */
export function filterDeviceInfoOnString(
  filterTerm: string,
  object: Record<string, any>,
  type: "pdi" | "ni"
) {
  return Object.entries(object).filter(([key, value]) => {
    const lowerCaseFilterTerm = filterTerm.toLowerCase();
    // This isn't super readable, but we need to filter not just on the object's keys, but also its translated value, since that's what the user sees
    // Check if the object's key includes the filter term
    const keyMatchRaw = key.toLowerCase().includes(lowerCaseFilterTerm);
    // Check if the object's stringified value includes the filter term
    const valueMatch = JSON.stringify(value ?? "")
      .toLowerCase()
      .includes(lowerCaseFilterTerm);
    // Check if the object's translated key includes the filter term ex. "Hardware Model" instead of "hardwareModel"
    if (key === "__typename") return false;
    const typeMatch =
      type === "pdi"
        ? pdiTranslations[key as keyof PrimaryDeviceInfo]
            .toLowerCase()
            .includes(lowerCaseFilterTerm)
        : type === "ni"
        ? niTranslations[key as keyof NetworkInfoTranslateKeys]
            .toLowerCase()
            .includes(lowerCaseFilterTerm)
        : false;
    return keyMatchRaw || valueMatch || typeMatch;
  });
}

export const DeviceInformation: React.FC<{
  deviceId: string;
  isPolyOs: boolean;
}> = ({ deviceId, isPolyOs }) => {
  const { t } = useTranslation();
  const [searchState, setSearchState] = useState<string>("");

  return (
    <>
      <div className="device-info-search" data-testid="device-info-search">
        <PolyIcon icon="search" />
        <input
          type="text"
          placeholder={t("SEARCH_DEVICE_INFORMATION")}
          aria-label={t("SEARCH_DEVICE_INFORMATION")}
          data-testid="search-device-info"
          value={searchState}
          onChange={e => setSearchState(e.target.value)}
          maxLength={MAX_SEARCH_CHARS}
        />
        {!!searchState.length && (
          <PolyIcon
            icon="clear_x"
            onClick={() => setSearchState("")}
            className="clear"
          />
        )}
      </div>
      <section className="pr-35 scrollable device-info-section">
        <ExpandableCard
          collapsedContent={
            <div className="category-title pl-5">
              {t("ADDITIONAL_DEVICE_INFORMATION")}
            </div>
          }
          dropdown
          expanded={false}
        >
          <PrimaryDeviceInfoSection
            searchState={searchState}
            deviceId={deviceId}
          />
        </ExpandableCard>
        <NetworkInfo searchState={searchState} deviceId={deviceId} />
        <ServiceInformation deviceId={deviceId} />
      </section>
    </>
  );
};
